import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import {
    getFullRestaurantMenu,
    getRestaurantMenu,
    getRestaurantMenuLanguages,
    getRestaurantMenuTranslations,
    IFullRestaurantMenu,
    IRestaurantMenu,
    IRestaurantMenuItemTranslation,
    IRestaurantMenuLanguage,
} from "@/shared/api/restaurant-menus"
import { Loader } from "@/shared/ui/loader"
import { PageHeader } from "@/widgets/page-header"
import { getApiErrorMessage } from "../lib/get-api-error-message"
import {
    RestaurantMenuDetailTab,
    RestaurantMenuDetailTabConfig,
    RestaurantMenuDetailTabState,
    RestaurantMenuEntityTarget,
    RestaurantMenuImagesTarget,
} from "../model/types"
import { MenuImagesModal } from "./menu-images-modal"
import { MenuLanguagesTab } from "./menu-languages-tab"
import { MenuTranslationsModal } from "./menu-translations-modal"
import { MenuTranslationsTab } from "./menu-translations-tab"
import { MenuTree } from "./menu-tree"

const tabs: RestaurantMenuDetailTabConfig[] = [
    { id: "tree", title: "Дерево меню" },
    { id: "languages", title: "Языки" },
    { id: "translations", title: "Переводы" },
]

const createTabState = <T,>(): RestaurantMenuDetailTabState<T> => ({
    isLoading: false,
    error: null,
    data: null,
})

const TabStateBlock = ({
    isLoading,
    error,
    onRetry,
    children,
}: {
    isLoading: boolean
    error: string | null
    onRetry: () => void
    children: ReactNode
}) => {
    if (isLoading) {
        return (
            <div className="info_panel">
                <Loader />
            </div>
        )
    }

    if (error) {
        return (
            <div className="info_panel flex items-center justify-between gap-4">
                <div className="text-color-danger">{error}</div>
                <button onClick={onRetry} className="form_handle_button !w-auto px-6">
                    Повторить
                </button>
            </div>
        )
    }

    return <>{children}</>
}

export const RestaurantMenuDetailPage = () => {
    const { menuId } = useParams()
    const [menu, setMenu] = useState<IRestaurantMenu | null>(null)
    const [isMenuLoading, setIsMenuLoading] = useState(false)
    const [menuError, setMenuError] = useState<string | null>(null)
    const [activeTab, setActiveTab] = useState<RestaurantMenuDetailTab>("tree")
    const [treeState, setTreeState] = useState(createTabState<IFullRestaurantMenu>)
    const [languagesState, setLanguagesState] = useState(createTabState<IRestaurantMenuLanguage[]>)
    const [translationsState, setTranslationsState] = useState(createTabState<IRestaurantMenuItemTranslation[]>)
    const [imagesTarget, setImagesTarget] = useState<RestaurantMenuImagesTarget | null>(null)
    const [translationsTarget, setTranslationsTarget] = useState<RestaurantMenuEntityTarget | null>(null)

    useEffect(() => {
        setMenu(null)
        setMenuError(null)
        setTreeState(createTabState<IFullRestaurantMenu>())
        setLanguagesState(createTabState<IRestaurantMenuLanguage[]>())
        setTranslationsState(createTabState<IRestaurantMenuItemTranslation[]>())
        setImagesTarget(null)
        setTranslationsTarget(null)
    }, [menuId])

    const fetchMenu = useCallback(async () => {
        if (!menuId) {
            setMenuError("Не указан идентификатор меню")
            return
        }

        try {
            setIsMenuLoading(true)
            setMenuError(null)
            const response = await getRestaurantMenu(menuId)
            setMenu(response.data)
        } catch (error) {
            setMenu(null)
            setMenuError(getApiErrorMessage(error))
        } finally {
            setIsMenuLoading(false)
        }
    }, [menuId])

    const fetchTree = useCallback(async () => {
        if (!menuId) {
            return
        }

        try {
            setTreeState((state) => ({ ...state, isLoading: true, error: null }))
            const response = await getFullRestaurantMenu(menuId)
            setTreeState({ isLoading: false, error: null, data: response.data })
        } catch (error) {
            setTreeState({ isLoading: false, error: getApiErrorMessage(error), data: null })
        }
    }, [menuId])

    const fetchLanguages = useCallback(async () => {
        if (!menuId) {
            return
        }

        try {
            setLanguagesState((state) => ({ ...state, isLoading: true, error: null }))
            const response = await getRestaurantMenuLanguages(menuId)
            setLanguagesState({ isLoading: false, error: null, data: response.data })
        } catch (error) {
            setLanguagesState({ isLoading: false, error: getApiErrorMessage(error), data: null })
        }
    }, [menuId])

    const fetchTranslations = useCallback(async () => {
        if (!menuId) {
            return
        }

        try {
            setTranslationsState((state) => ({ ...state, isLoading: true, error: null }))
            const response = await getRestaurantMenuTranslations(menuId)
            setTranslationsState({ isLoading: false, error: null, data: response.data })
        } catch (error) {
            setTranslationsState({ isLoading: false, error: getApiErrorMessage(error), data: null })
        }
    }, [menuId])

    useEffect(() => {
        fetchMenu()
    }, [fetchMenu])

    useEffect(() => {
        if (activeTab === "tree" && !treeState.data && !treeState.isLoading && !treeState.error) {
            fetchTree()
        }

        if (activeTab === "languages" && !languagesState.data && !languagesState.isLoading && !languagesState.error) {
            fetchLanguages()
        }

        if (
            activeTab === "translations" &&
            !translationsState.data &&
            !translationsState.isLoading &&
            !translationsState.error
        ) {
            fetchTranslations()
        }
    }, [
        activeTab,
        fetchLanguages,
        fetchTranslations,
        fetchTree,
        languagesState.data,
        languagesState.error,
        languagesState.isLoading,
        translationsState.data,
        translationsState.error,
        translationsState.isLoading,
        treeState.data,
        treeState.error,
        treeState.isLoading,
    ])

    const languagesForTranslations = useMemo(() => {
        return languagesState.data ?? treeState.data?.languages ?? menu?.languages ?? []
    }, [languagesState.data, menu?.languages, treeState.data?.languages])

    const handleTranslationsChanged = async () => {
        await Promise.all([
            fetchTranslations(),
            fetchTree(),
        ])
    }

    const renderActiveTab = () => {
        if (!menuId) {
            return <div className="info_panel text-color-danger">Не указан идентификатор меню</div>
        }

        if (activeTab === "tree") {
            return (
                <TabStateBlock isLoading={treeState.isLoading} error={treeState.error} onRetry={fetchTree}>
                    {treeState.data ? (
                        <MenuTree
                            menuId={menuId}
                            data={treeState.data}
                            onRefresh={fetchTree}
                            onOpenImages={setImagesTarget}
                            onOpenTranslations={setTranslationsTarget}
                        />
                    ) : (
                        <div className="info_panel">Данные ещё не загружены</div>
                    )}
                </TabStateBlock>
            )
        }

        if (activeTab === "languages") {
            return (
                <TabStateBlock isLoading={languagesState.isLoading} error={languagesState.error} onRetry={fetchLanguages}>
                    {languagesState.data ? (
                        <MenuLanguagesTab
                            menuId={menuId}
                            languages={languagesState.data}
                            onChange={(languages) => setLanguagesState({ isLoading: false, error: null, data: languages })}
                        />
                    ) : (
                        <div className="info_panel">Данные ещё не загружены</div>
                    )}
                </TabStateBlock>
            )
        }

        return (
            <TabStateBlock isLoading={translationsState.isLoading} error={translationsState.error} onRetry={fetchTranslations}>
                {translationsState.data ? (
                    <MenuTranslationsTab
                        translations={translationsState.data}
                        onOpenTranslations={setTranslationsTarget}
                    />
                ) : (
                    <div className="info_panel">Данные ещё не загружены</div>
                )}
            </TabStateBlock>
        )
    }

    return (
        <div className="_page_container">
            <PageHeader title={menu?.name || "Меню ресторанов"} />
            <div className="flexbox">
                {isMenuLoading && (
                    <div className="info_panel">
                        <Loader />
                    </div>
                )}

                {!isMenuLoading && menuError && (
                    <div className="info_panel flex items-center justify-between gap-4">
                        <div className="text-color-danger">{menuError}</div>
                        <button onClick={fetchMenu} className="form_handle_button !w-auto px-6">
                            Повторить
                        </button>
                    </div>
                )}

                {!isMenuLoading && !menuError && menu && (
                    <>
                        <div className="flex flex-wrap gap-2 border-b border-[#D9E3E1]">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    className={[
                                        "px-4 py-3 text-base transition border-b-2",
                                        activeTab === tab.id
                                            ? "border-color-dark text-color-dark font-medium"
                                            : "border-transparent text-[#828282] hover:text-color-dark",
                                    ].join(" ")}
                                >
                                    {tab.title}
                                </button>
                            ))}
                        </div>

                        {renderActiveTab()}
                    </>
                )}
            </div>

            {menuId && (
                <>
                    <MenuImagesModal
                        isOpen={Boolean(imagesTarget)}
                        menuId={menuId}
                        target={imagesTarget}
                        onClose={() => setImagesTarget(null)}
                        onChanged={fetchTree}
                    />

                    <MenuTranslationsModal
                        isOpen={Boolean(translationsTarget)}
                        menuId={menuId}
                        target={translationsTarget}
                        languages={languagesForTranslations}
                        translations={translationsState.data}
                        onClose={() => setTranslationsTarget(null)}
                        onChanged={handleTranslationsChanged}
                    />
                </>
            )}
        </div>
    )
}
